import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, addDays, subDays, startOfWeek, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isSameMonth, isWeekend, addMonths, subMonths } from 'date-fns';
import { Home, Plus, ChevronLeft, ChevronRight, ChevronDown, Calendar as CalendarIcon, X, Package, Trash2, Check, XCircle, ArrowRightLeft, Building2, Clock } from 'lucide-react';
import AppHeader from '../components/AppHeader';
import GoodsInPOPanel from '../components/GoodsInPOPanel';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { API } from '../App';
import api from '../services/api';
import { toast } from 'sonner';
import { useBranch } from '../context/BranchContext';

const enconOrange = '#ee7203';
const enconBlack = '#231f20';
const enconPurple = '#8B5CF6';
const enconPink = '#EC4899'; // Pink for Stock OUT transfers

// Branch list for transfers (same as StockTransfer page)
const BRANCHES = [
  { id: 'birmingham', name: 'Encon Birmingham', alternateName: 'Perry Barr', address: '21 Gavin Way, Perry Barr, WS10 0QP', email: 'enconbirmingham@encon.co.uk', branchCode: 'C30' },
  { id: 'nottingham', name: 'Encon Nottingham', alternateName: 'Bulwell', address: '4 Martin Close, Bulwell, Nottingham, NG6 8UW', branchCode: null },
  { id: 'west-bromwich', name: 'Nevill Long West Brom', alternateName: 'West Bromwich', address: 'Unit 5, Hilltop Trading Estate, Shaw Street, West Bromwich, B70 0TX', branchCode: 'E70' },
  { id: 'northampton', name: 'Encon Northampton', alternateName: 'Brackmills', address: 'Unit 1, Burryport Road, Brackmills Ind Est, Northampton, NN4 7BB', branchCode: null },
  { id: 'thetford', name: 'Nevill Long Thetford', alternateName: 'Norfolk', address: '10 Burrell Way Trade Park, Thetford, Norfolk, IP24 3RW', branchCode: null },
  { id: 'newmarket', name: 'Encon Newmarket', alternateName: 'Studlands Park', address: 'Unit 13, Willie Snaithe Road, Studlands Park Ind Est, Newmarket, CB8 7AU', branchCode: null },
  { id: 'leeds', name: 'Encon Leeds', alternateName: 'Low Fields', address: 'Unit 3, Benyon Park Way, Off Low Fields Road, Leeds, LS12 6DP', branchCode: null },
  { id: 'chorley', name: 'Encon North West', alternateName: 'Chorley', address: 'Revolution Park, Buckshaw Avenue, Chorley, PR7 7DW', branchCode: null },
  { id: 'newcastle', name: 'Encon Newcastle', alternateName: 'Tyne Tunnel', address: 'Unit E2, High Flatworth, Tyne Tunnel Trade Estate, Newcastle, NE29 7UZ', branchCode: null },
  { id: 'washington', name: 'Washington Technical', alternateName: 'Pattison', address: '2 Faraday Close, Pattison North Ind Est, Washington, NE38 8QJ', branchCode: null },
  { id: 'dundee', name: 'Encon Dundee', alternateName: 'Wester Gourdie', address: 'Block 9, Myrekirk Road, Wester Gourdie, Dundee, DD2 4SH', branchCode: null },
  { id: 'glasgow', name: 'Encon Glasgow', alternateName: 'Cambuslang', address: '80 Cambuslang Road, Cambuslang Investment Park, Glasgow, G32 8NB', branchCode: null },
  { id: 'plymouth', name: 'Encon Plymouth', alternateName: 'Langage', address: 'Unit 4, Langage One, Western Wood Way, Langage Science Park, Plymouth, PL7 5BE', branchCode: null },
  { id: 'fareham', name: 'Encon Fareham', alternateName: 'Gosport', address: 'Unit 3, Fareham Trade Park, Lederle Lane, Gosport, PO13 0FE', branchCode: null },
  { id: 'cardiff', name: 'Encon Cardiff', alternateName: 'Ocean Way', address: 'Unit 4, Triden Park, Ocean Way, Cardiff, CF24 5EP', branchCode: null },
  { id: 'bristol', name: 'Encon Bristol', alternateName: 'Avonmouth', address: 'Century Park, Chittening Ind Estate, Avonmouth, Bristol, BS11 0YB', branchCode: null },
  { id: 'witney', name: 'Encon Witney', alternateName: 'Stanton Harcourt', address: 'Unit 5, Stanton Harcourt Ind Est, Stanton Harcourt, Witney, OX29 5UX', branchCode: null },
  { id: 'oxford-technical', name: 'Oxford Technical', alternateName: 'Long Crendon', address: 'Unit 8 Hikers Way, Crendon Ind Est, Long Crendon, Buckinghamshire, HP18 9RW', branchCode: null },
  { id: 'east-london', name: 'Encon East London', alternateName: 'Dagenham', address: 'Binary Park, Choats Road, Dagenham, East London, RM9 6RJ', branchCode: null },
  { id: 'maidstone', name: 'Encon Maidstone', alternateName: 'Aylesford', address: 'Units F1 & F2, St Michaels Close, Forstal Ind Est, Aylesford, ME20 7BU', branchCode: null },
  { id: 'west-london', name: 'Encon West London', alternateName: 'Feltham', address: 'Units 6-8, The Griffin Centre, Staines Road, Feltham, TW14 0HS', branchCode: null }
];

const GoodsIn = ({ user }) => {
  const navigate = useNavigate();
  const calendarRef = useRef(null);
  const [deliveries, setDeliveries] = useState([]);
  const [transfers, setTransfers] = useState([]); // Transfer In entries
  const [loading, setLoading] = useState(true);
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [viewMode, setViewMode] = useState('week'); // 'week' or 'month'
  const [isTransferMode, setIsTransferMode] = useState(false); // Toggle for Transfer In mode
  const [expandedTransferGroup, setExpandedTransferGroup] = useState(null); // For grouped transfer modal
  const [branchSearch, setBranchSearch] = useState(''); // Search filter for branch dropdown
  const [showBranchDropdown, setShowBranchDropdown] = useState(false); // Toggle branch dropdown visibility
  const [showDayModal, setShowDayModal] = useState(false); // Modal for viewing all entries on a day
  const [selectedDayEntries, setSelectedDayEntries] = useState([]); // Entries for the selected day
  const [selectedDayDate, setSelectedDayDate] = useState(null); // The date for the day modal
  
  // Get branch context for data isolation
  const { getEffectiveBranchId } = useBranch();
  const effectiveBranchId = getEffectiveBranchId();
  
  // Branch-aware accent color (purple for E70, orange for others)
  const accentColor = effectiveBranchId === 'E70' ? enconPurple : enconOrange;
  
  // Form state for adding/editing entry
  const [formEntry, setFormEntry] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    supplier_name: '',
    branch_id: '', // For transfer mode
    order_number: '',
    carrier_details: '', // For transfer mode
    notes: '',
    is_transfer: false,
    stock_direction: 'in' // 'in' or 'out' for transfers
  });

  // Get today and working week dates
  const today = new Date();
  const getWorkingWeekDates = () => {
    const dates = [];
    let currentDate = today;
    
    // Find the current working day or next if weekend
    while (isWeekend(currentDate)) {
      currentDate = addDays(currentDate, 1);
    }
    
    // Get 5 working days starting from current
    let count = 0;
    let checkDate = currentDate;
    while (count < 5) {
      if (!isWeekend(checkDate)) {
        dates.push(checkDate);
        count++;
      }
      checkDate = addDays(checkDate, 1);
    }
    return dates;
  };
  
  const workingWeekDates = getWorkingWeekDates();

  // Calculate max deliveries for uniform card height
  const maxDeliveriesInWeek = useMemo(() => {
    return Math.max(
      ...workingWeekDates.map(date => 
        deliveries.filter(d => isSameDay(new Date(d.date), date) && !isWeekend(new Date(d.date))).length
      ),
      1
    );
  }, [workingWeekDates, deliveries]);

  // Fetch deliveries - filtered by branch
  const fetchDeliveries = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const url = effectiveBranchId 
        ? `${API}/goods-in/deliveries?view_as_branch=${effectiveBranchId}`
        : `${API}/goods-in/deliveries`;
      const response = await api.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const allDeliveries = response.data.deliveries || [];
      
      // Separate regular deliveries from transfers
      setDeliveries(allDeliveries.filter(d => !d.is_transfer));
      setTransfers(allDeliveries.filter(d => d.is_transfer));
    } catch (error) {
      console.error('Failed to fetch deliveries:', error);
      setDeliveries([]);
      setTransfers([]);
    } finally {
      setLoading(false);
    }
  }, [effectiveBranchId]);

  useEffect(() => {
    fetchDeliveries();
  }, [fetchDeliveries]);

  // Filter deliveries for today (exclude weekends from today view too)
  const todayDeliveries = deliveries.filter(d => 
    isSameDay(new Date(d.date), today)
  );

  // Get deliveries for a specific date
  const getDeliveriesForDate = (date) => {
    return deliveries.filter(d => isSameDay(new Date(d.date), date));
  };

  // Get transfers for a specific date
  // Transfers stay on their booked date - they do NOT move forward
  const getTransfersForDate = (date) => {
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    
    return transfers.filter(t => {
      const transferDate = new Date(t.date);
      transferDate.setHours(0, 0, 0, 0);
      
      // If complete, show on completed date
      if (t.status === 'complete') {
        const completedDate = new Date(t.status_updated_at || t.date);
        completedDate.setHours(0, 0, 0, 0);
        return isSameDay(completedDate, checkDate);
      }
      
      // All transfers stay on their original booked date
      return isSameDay(transferDate, checkDate);
    });
  };

  // Group transfers by supplier/branch name
  // Group transfers by branch AND stock direction (so IN and OUT from same branch show separately)
  const groupTransfersByBranch = (transferList) => {
    const groups = {};
    transferList.forEach(t => {
      const branchName = t.supplier_name || 'Unknown';
      const direction = t.stock_direction || 'in';
      // Create unique key combining branch name and direction
      const key = `${branchName}::${direction}`;
      if (!groups[key]) {
        groups[key] = {
          branchName,
          direction,
          transfers: []
        };
      }
      groups[key].transfers.push(t);
    });
    return groups;
  };

  // Get transfer color based on stock_direction
  const getTransferColor = (transfer, isToday = false) => {
    if (transfer.status === 'complete') return '#22c55e'; // Green for complete
    if (transfer.status === 'failed') return '#ef4444'; // Red for failed
    // For pending transfers
    if (transfer.stock_direction === 'out') return enconPink; // Turquoise for OUT
    return '#3b82f6'; // Blue for IN
  };

  // Format creator name from email - show display name if available, otherwise first part of email
  const formatCreatorName = (createdBy) => {
    if (!createdBy) return '';
    // If it's an email, extract the part before @
    if (createdBy.includes('@')) {
      const namePart = createdBy.split('@')[0];
      // Capitalize first letter of each part separated by dots
      return namePart.split('.').map(part => 
        part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
      ).join('.');
    }
    return createdBy;
  };

  // Get today's transfers (booked for today only)
  const todayTransfers = transfers.filter(t => {
    const transferDate = new Date(t.date);
    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);
    transferDate.setHours(0, 0, 0, 0);
    if (t.status === 'complete') {
      return isSameDay(new Date(t.status_updated_at || t.date), todayDate);
    }
    return isSameDay(transferDate, todayDate);
  });

  const upcomingTransfers = transfers.filter(t => {
    const transferDate = new Date(t.date);
    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);
    transferDate.setHours(0, 0, 0, 0);
    return t.status !== 'complete' && transferDate > todayDate;
  });

  // Past transfers: from the past 5 working days (before today)
  const pastTransfers = useMemo(() => {
    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);
    // Calculate 5 working days back
    let count = 0;
    let d = new Date(todayDate);
    while (count < 5) {
      d = addDays(d, -1);
      if (!isWeekend(d)) count++;
    }
    const cutoff = new Date(d);
    cutoff.setHours(0, 0, 0, 0);
    
    return transfers.filter(t => {
      const transferDate = new Date(t.date);
      transferDate.setHours(0, 0, 0, 0);
      // Past = booked before today AND on/after cutoff
      if (t.status === 'complete') {
        const completedDate = new Date(t.status_updated_at || t.date);
        completedDate.setHours(0, 0, 0, 0);
        return completedDate < todayDate && completedDate >= cutoff;
      }
      return transferDate < todayDate && transferDate >= cutoff;
    });
  }, [transfers]);

  const [showPastTransfersModal, setShowPastTransfersModal] = useState(false);

  // Format ordinal suffix for date
  const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  // Open add modal
  const openAddModal = () => {
    // Find next working day for default date
    let defaultDate = new Date();
    while (isWeekend(defaultDate)) {
      defaultDate = addDays(defaultDate, 1);
    }
    setFormEntry({
      date: format(defaultDate, 'yyyy-MM-dd'),
      supplier_name: '',
      branch_id: '',
      order_number: '',
      carrier_details: '',
      notes: '',
      is_transfer: false,
      stock_direction: 'in'
    });
    setIsTransferMode(false);
    setShowAddModal(true);
  };

  // Open edit modal
  const openEditModal = (delivery) => {
    setEditingEntry(delivery);
    setFormEntry({
      date: delivery.date,
      supplier_name: delivery.supplier_name,
      branch_id: delivery.source_branch_id || delivery.branch_id || '',
      order_number: delivery.order_number,
      carrier_details: delivery.carrier_details || '',
      notes: delivery.notes || '',
      is_transfer: delivery.is_transfer || false,
      stock_direction: delivery.stock_direction || 'in'
    });
    setIsTransferMode(delivery.is_transfer || false);
    setBranchSearch('');
    setShowBranchDropdown(false);
    setShowEditModal(true);
  };

  // Add new delivery
  const handleAddDelivery = async () => {
    // No validation required - all fields are optional

    // Check if date is weekend (only if date is set)
    if (formEntry.date && isWeekend(new Date(formEntry.date))) {
      toast.error('Cannot schedule deliveries on weekends');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      // Include branch parameter for data isolation
      const url = effectiveBranchId 
        ? `${API}/goods-in/deliveries?view_as_branch=${effectiveBranchId}`
        : `${API}/goods-in/deliveries`;
      
      // Prepare data based on mode - keep values as entered (or empty)
      const submitData = {
        ...formEntry,
        is_transfer: isTransferMode,
        supplier_name: isTransferMode 
          ? (BRANCHES.find(b => b.id === formEntry.branch_id)?.name || formEntry.branch_id || '')
          : (formEntry.supplier_name || '')
      };
      
      const response = await api.post(url, submitData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Trigger immediate update on warehouse page via localStorage event
      const newDeliveryEvent = {
        type: isTransferMode ? 'new_transfer' : 'new_delivery',
        supplier: submitData.supplier_name,
        order_number: formEntry.order_number,
        timestamp: Date.now()
      };
      localStorage.setItem('goods_in_new_delivery', JSON.stringify(newDeliveryEvent));
      
      toast.success(isTransferMode ? 'Transfer added successfully' : 'Delivery added successfully');
      setShowAddModal(false);
      setBranchSearch('');
      setShowBranchDropdown(false);
      fetchDeliveries();
    } catch (error) {
      console.error('Failed to add delivery:', error);
      toast.error('Failed to add delivery');
    }
  };

  // Update delivery
  const handleUpdateDelivery = async () => {
    // No validation required - all fields are optional

    try {
      const token = localStorage.getItem('token');
      
      // Prepare update data
      const updateData = {
        ...formEntry,
        is_transfer: isTransferMode,
        // If switching to transfer mode, get supplier name from branch (or keep empty)
        supplier_name: isTransferMode 
          ? (BRANCHES.find(b => b.id === formEntry.branch_id)?.name || formEntry.supplier_name || '')
          : (formEntry.supplier_name || '')
      };
      
      await api.put(`${API}/goods-in/deliveries/${editingEntry.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(isTransferMode ? 'Transfer updated successfully' : 'Delivery updated successfully');
      setShowEditModal(false);
      setEditingEntry(null);
      setBranchSearch('');
      setShowBranchDropdown(false);
      fetchDeliveries();
    } catch (error) {
      console.error('Failed to update delivery:', error);
      toast.error('Failed to update delivery');
    }
  };

  // Delete delivery
  const handleDeleteDelivery = async () => {
    try {
      const token = localStorage.getItem('token');
      await api.delete(`${API}/goods-in/deliveries/${editingEntry.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Delivery deleted successfully');
      setShowDeleteConfirm(false);
      setShowEditModal(false);
      setEditingEntry(null);
      fetchDeliveries();
    } catch (error) {
      console.error('Failed to delete delivery:', error);
      toast.error('Failed to delete delivery');
    }
  };

  // Update delivery status (complete/failed/pending)
  const handleUpdateStatus = async (status) => {
    if (!editingEntry) return;
    
    try {
      await api.patch(`/api/goods-in/deliveries/${editingEntry.id}/status`, {
        status: status
      });
      
      const statusLabel = status === 'pending' ? 'cleared' : status;
      toast.success(`Delivery marked as ${statusLabel}`);
      setShowEditModal(false);
      setEditingEntry(null);
      fetchDeliveries();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };

  // Calendar helpers
  const getCalendarDays = () => {
    const start = startOfMonth(calendarMonth);
    const end = endOfMonth(calendarMonth);
    const startWeek = startOfWeek(start, { weekStartsOn: 1 });
    const endWeek = addDays(startOfWeek(end, { weekStartsOn: 1 }), 6);
    
    return eachDayOfInterval({ start: startWeek, end: endWeek });
  };

  // Get status color
  const getStatusColor = (status) => {
    if (status === 'complete') return '#22c55e';
    if (status === 'failed') return '#ef4444';
    return enconOrange;
  };

  // Open day modal with all entries for a specific date
  const openDayModal = (date, entries) => {
    setSelectedDayDate(date);
    setSelectedDayEntries(entries);
    setShowDayModal(true);
  };

  // Render delivery card
  const renderDeliveryCard = (delivery, size = 'normal') => {
    const statusColor = getStatusColor(delivery.status);
    const isComplete = delivery.status === 'complete';
    const isFailed = delivery.status === 'failed';
    const creatorName = formatCreatorName(delivery.created_by);
    
    return (
      <div
        key={delivery.id}
        onClick={() => openEditModal(delivery)}
        className={`cursor-pointer transition-all hover:shadow-md rounded-lg border-l-4 ${
          size === 'small' ? 'p-2 text-xs' : 'p-2 text-sm'
        }`}
        style={{ 
          borderLeftColor: statusColor,
          backgroundColor: isComplete ? 'rgba(34, 197, 94, 0.1)' : 
                          isFailed ? 'rgba(239, 68, 68, 0.1)' : 
                          'rgba(238, 114, 3, 0.05)'
        }}
      >
        <div className="flex items-center gap-1">
          {isComplete && <Check className="w-3 h-3 text-green-500 flex-shrink-0" />}
          {isFailed && <XCircle className="w-3 h-3 text-red-500 flex-shrink-0" />}
          <span className="font-semibold" style={{ color: statusColor }}>
            {delivery.supplier_name}
          </span>
          {delivery.source === 'purchase_order_email' && (
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full ml-1 flex-shrink-0" style={{ background: '#dcfce7', color: '#15803d' }}>PO</span>
          )}
        </div>
        <div className="text-gray-500">
          {delivery.order_number ? `#${delivery.order_number}` : ''}
        </div>
        {creatorName && (
          <div className="text-gray-400 text-[10px] mt-0.5">
            Added by: {creatorName}
          </div>
        )}
        {delivery.notes && (
          <div className="text-gray-400 text-[10px] mt-1 line-clamp-2 italic">
            {delivery.notes.split('\n')[0]}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#e5e7eb' }}>
      {/* Global Header */}
      <AppHeader 
        user={user}
        onLogout={() => {}}
        subHeaderLeft={
          <div className="flex items-center gap-1.5 rounded-full px-3 py-1 flex-shrink-0" style={{ background: 'rgba(0,0,0,0.22)' }}>
            <span className="text-white font-bold text-xs sm:text-sm whitespace-nowrap">Goods In</span>
          </div>
        }
        mobileMenuButtons={[
          { icon: Plus, label: 'Add Entry', onClick: openAddModal, color: accentColor }
        ]}
      />

      {/* Full Screen Calendar Overlay */}
      {showCalendar && (
        <div 
          className="fixed inset-0 z-50 bg-white"
          style={{ top: '160px' }}
        >
          <div className="h-full overflow-auto p-4 md:p-6">
            {/* Calendar Header with Close Button */}
            <div className="flex items-center justify-between mb-6">
              <button
                onClick={() => setCalendarMonth(subMonths(calendarMonth, 1))}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <ChevronLeft className="w-6 h-6" style={{ color: accentColor }} />
              </button>
              
              <div className="flex items-center gap-4">
                <h3 className="text-xl md:text-2xl font-bold" style={{ color: enconBlack }}>
                  {format(calendarMonth, 'MMMM yyyy')}
                </h3>
                
                {/* Close Calendar Button */}
                <button
                  onClick={() => setShowCalendar(false)}
                  className="px-4 py-2 rounded-xl font-semibold border-2 transition-all hover:shadow-md flex items-center gap-2"
                  style={{ 
                    backgroundColor: 'white',
                    borderColor: accentColor,
                    color: accentColor
                  }}
                >
                  <X className="w-4 h-4" />
                  Close
                </button>
              </div>
              
              <button
                onClick={() => setCalendarMonth(addMonths(calendarMonth, 1))}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <ChevronRight className="w-6 h-6" style={{ color: accentColor }} />
              </button>
            </div>

            {/* Calendar Grid */}
            {(() => {
              const calendarDays = getCalendarDays();
              // Group days into rows of 7 (weeks)
              const weeks = [];
              for (let i = 0; i < calendarDays.length; i += 7) {
                weeks.push(calendarDays.slice(i, i + 7));
              }
              
              return (
                <div className="space-y-1 md:space-y-2">
                  {/* Day Headers */}
                  <div className="grid grid-cols-7 gap-1 md:gap-2">
                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                      <div key={day} className={`text-center text-sm font-semibold py-2 ${
                        day === 'Sat' || day === 'Sun' ? 'text-gray-300' : 'text-gray-500'
                      }`}>
                        {day}
                      </div>
                    ))}
                  </div>
                  
                  {/* Calendar Weeks - each row expands to fit max deliveries in that week */}
                  {weeks.map((week, weekIdx) => {
                    // Find max deliveries in this week row
                    const maxInWeek = Math.max(...week.map(date => getDeliveriesForDate(date).length), 1);
                    // Calculate row height: base 60px + 28px per delivery (minimum 100px)
                    const rowMinHeight = Math.max(100, 60 + maxInWeek * 28);
                    
                    return (
                      <div key={weekIdx} className="grid grid-cols-7 gap-1 md:gap-2" style={{ minHeight: `${rowMinHeight}px` }}>
                        {week.map((date, dayIdx) => {
                          const dayDeliveries = getDeliveriesForDate(date);
                          const isCurrentMonth = isSameMonth(date, calendarMonth);
                          const isToday = isSameDay(date, today);
                          const isWeekendDay = isWeekend(date);
                          
                          return (
                            <div
                              key={dayIdx}
                              onClick={() => dayDeliveries.length > 0 && openDayModal(date, dayDeliveries)}
                              className={`p-2 rounded-lg border transition-all cursor-pointer hover:shadow-md ${
                                isCurrentMonth ? 'bg-white' : 'bg-gray-50'
                              } ${isToday ? 'ring-2' : ''} ${
                                isWeekendDay ? 'bg-gray-100 opacity-60' : ''
                              }`}
                              style={{ 
                                borderColor: isToday ? accentColor : '#e5e7eb',
                                ringColor: accentColor
                              }}
                            >
                              <div className={`text-sm font-semibold mb-1 ${
                                isCurrentMonth ? 'text-gray-700' : 'text-gray-400'
                              } ${isWeekendDay ? 'text-gray-400' : ''}`}
                              style={{ color: isToday ? accentColor : undefined }}
                              >
                                {format(date, 'd')}
                              </div>
                              
                              {/* Show ALL deliveries - no truncation */}
                              <div className="space-y-1">
                                {dayDeliveries.map((delivery) => (
                                  <div
                                    key={delivery.id}
                                    onClick={(e) => { e.stopPropagation(); openEditModal(delivery); }}
                                    className="text-[11px] px-1.5 py-1 rounded truncate text-white cursor-pointer hover:opacity-80 flex items-center gap-0.5"
                                    style={{ backgroundColor: getStatusColor(delivery.status) }}
                                    title={`${delivery.supplier_name} - ${delivery.order_number}`}
                                  >
                                    {delivery.status === 'complete' && <Check className="w-2.5 h-2.5" />}
                                    {delivery.status === 'failed' && <XCircle className="w-2.5 h-2.5" />}
                                    {delivery.supplier_name}
                                    {delivery.source === 'purchase_order_email' && (
                                      <span className="ml-0.5 text-[8px] font-bold px-1 py-0.5 rounded flex-shrink-0" style={{ background: '#dcfce7', color: '#15803d' }}>PO</span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Main Content - reduced top padding for small gap */}
      <div className="flex-1 px-4 md:px-6 pb-6 pt-[20px] bg-gray-100 min-h-screen">
        
        {/* Transfers Section - Title on left side */}
        {(todayTransfers.length > 0 || upcomingTransfers.length > 0 || pastTransfers.length > 0) && (
          <div className="flex mb-3">
            {/* Vertical Title - rounded corners face outward (toward page edge) */}
            {/* Note: Using rounded-r because element is rotated 180deg */}
            <div 
              className="flex items-center justify-center bg-blue-500 rounded-r-xl px-2"
              style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', transform: 'rotate(180deg)' }}
            >
              <span className="text-white font-bold text-sm tracking-wider flex items-center gap-2">
                <ArrowRightLeft className="w-4 h-4" />
                TRANSFERS
              </span>
            </div>
            
            {/* Transfers Content - rounded corners on right edge */}
            <div className="flex-1 bg-white rounded-r-xl shadow-md p-3">
              <div className="flex gap-3 overflow-x-auto pb-1 items-center">
                
                {/* Past Week's Transfers Button - always first */}
                {pastTransfers.length > 0 && (
                  <div
                    onClick={() => setShowPastTransfersModal(true)}
                    className="flex-shrink-0 w-56 p-3 rounded-xl border-2 transition-all hover:shadow-lg hover:scale-[1.02] cursor-pointer flex flex-col items-center justify-center"
                    style={{ borderColor: enconOrange, backgroundColor: 'rgba(238, 114, 3, 0.08)', minHeight: '100px' }}
                    data-testid="past-transfers-btn"
                  >
                    <Clock className="w-6 h-6 mb-1" style={{ color: enconOrange }} />
                    <span className="font-bold text-sm text-center" style={{ color: enconOrange }}>
                      Past Week's Transfers
                    </span>
                    <span className="text-white text-xs font-bold px-3 py-0.5 rounded-full mt-1" style={{ backgroundColor: enconOrange }}>
                      {pastTransfers.length}
                    </span>
                  </div>
                )}

                {/* Separator after Past Transfers button */}
                {pastTransfers.length > 0 && (todayTransfers.length > 0 || upcomingTransfers.length > 0) && (
                  <div className="flex-shrink-0 flex items-center px-1">
                    <div className="text-2xl text-gray-300 font-light">||</div>
                  </div>
                )}

                {/* Today's Transfers - Grouped by Branch + Direction */}
                {Object.entries(groupTransfersByBranch(todayTransfers)).map(([groupKey, group]) => {
                  const { branchName, direction, transfers: branchTransfers } = group;
                  // Card color based on direction (since all in group have same direction now)
                  const cardColor = branchTransfers.every(t => t.status === 'complete') ? '#22c55e' : 
                                   branchTransfers.some(t => t.status === 'failed') ? '#ef4444' : 
                                   direction === 'out' ? enconPink : '#3b82f6';
                  const bgColor = branchTransfers.every(t => t.status === 'complete') ? 'rgba(34, 197, 94, 0.1)' :
                                 branchTransfers.some(t => t.status === 'failed') ? 'rgba(239, 68, 68, 0.1)' :
                                 direction === 'out' ? 'rgba(236, 72, 153, 0.1)' : 'rgba(59, 130, 246, 0.1)';
                  
                  return (
                    <div
                      key={`today-${groupKey}`}
                      onClick={() => branchTransfers.length > 1 ? setExpandedTransferGroup({ name: branchName, transfers: branchTransfers }) : openEditModal(branchTransfers[0])}
                      className="flex-shrink-0 w-56 p-3 rounded-xl border-2 transition-all hover:shadow-md cursor-pointer relative"
                      style={{ 
                        borderColor: cardColor,
                        backgroundColor: bgColor
                      }}
                    >
                      {branchTransfers.length > 1 && (
                        <span 
                          className="absolute top-2 right-2 text-white text-xs font-bold px-2 py-0.5 rounded-full"
                          style={{ backgroundColor: cardColor }}
                        >
                          {branchTransfers.length}
                        </span>
                      )}
                      <div className="flex items-center gap-2 mb-1">
                        {branchTransfers.every(t => t.status === 'complete') && <Check className="w-4 h-4 text-green-500" />}
                        {branchTransfers.some(t => t.status === 'failed') && <XCircle className="w-4 h-4 text-red-500" />}
                        {!branchTransfers.every(t => t.status === 'complete') && !branchTransfers.some(t => t.status === 'failed') && (
                          <ArrowRightLeft className="w-4 h-4" style={{ color: cardColor }} />
                        )}
                        <span className="font-bold text-sm truncate" style={{ color: enconBlack }}>
                          {branchName}
                        </span>
                      </div>
                      <div className="text-xs text-gray-600">
                        {branchTransfers.length === 1 ? (branchTransfers[0].order_number ? `Expense: ${branchTransfers[0].order_number}` : '') : `${branchTransfers.length} transfers`}
                      </div>
                      {/* Creator name for single transfer */}
                      {branchTransfers.length === 1 && branchTransfers[0].created_by && (
                        <div className="text-[10px] text-gray-500">
                          Added by: {formatCreatorName(branchTransfers[0].created_by)}
                        </div>
                      )}
                      {/* Stock IN/OUT Tag - always show since all in group have same direction */}
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs font-semibold" style={{ color: cardColor }}>Today</span>
                        <span 
                          className="text-[10px] font-bold px-1.5 py-0.5 rounded text-white"
                          style={{ backgroundColor: direction === 'out' ? enconPink : '#3b82f6' }}
                        >
                          STOCK {direction === 'out' ? 'OUT' : 'IN'}
                        </span>
                      </div>
                    </div>
                  );
                })}
                
                {/* Separator between Today and Upcoming */}
                {todayTransfers.length > 0 && upcomingTransfers.length > 0 && (
                  <div className="flex-shrink-0 flex items-center px-1">
                    <div className="text-2xl text-gray-300 font-light">||</div>
                  </div>
                )}
                
                {/* Upcoming Transfers - Grouped by Branch + Direction (Grey cards, colored tags) */}
                {Object.entries(groupTransfersByBranch(upcomingTransfers)).map(([groupKey, group]) => {
                  const { branchName, direction, transfers: branchTransfers } = group;
                  
                  return (
                    <div
                      key={`upcoming-${groupKey}`}
                      onClick={() => branchTransfers.length > 1 ? setExpandedTransferGroup({ name: branchName, transfers: branchTransfers }) : openEditModal(branchTransfers[0])}
                      className="flex-shrink-0 w-56 p-3 rounded-xl border-2 transition-all hover:shadow-md cursor-pointer relative opacity-60"
                      style={{ 
                        borderColor: '#9ca3af',
                        backgroundColor: 'rgba(156, 163, 175, 0.1)'
                      }}
                    >
                      {branchTransfers.length > 1 && (
                        <span className="absolute top-2 right-2 bg-gray-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                          {branchTransfers.length}
                        </span>
                      )}
                      <div className="flex items-center gap-2 mb-1">
                        <ArrowRightLeft className="w-4 h-4 text-gray-500" />
                        <span className="font-bold text-sm truncate text-gray-600">
                          {branchName}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500">
                        {branchTransfers.length === 1 ? (branchTransfers[0].order_number ? `Expense: ${branchTransfers[0].order_number}` : '') : `${branchTransfers.length} transfers`}
                      </div>
                      {/* Creator name for single transfer */}
                      {branchTransfers.length === 1 && branchTransfers[0].created_by && (
                        <div className="text-[10px] text-gray-500">
                          Added by: {formatCreatorName(branchTransfers[0].created_by)}
                        </div>
                      )}
                      {/* Stock IN/OUT Tag with correct color on grey card */}
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-500">
                          {format(new Date(branchTransfers[0].date), 'EEE dd/MM')}
                        </span>
                        <span 
                          className="text-[10px] font-bold px-1.5 py-0.5 rounded text-white"
                          style={{ backgroundColor: direction === 'out' ? enconPink : '#3b82f6' }}
                        >
                          STOCK {direction === 'out' ? 'OUT' : 'IN'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
        
        {/* View Controls Row — week/month toggle + Add button above views */}
        <div className="flex items-center justify-between mb-3">
          {/* Week/Month Toggle */}
          <div className="flex rounded-xl overflow-hidden border-2" style={{ borderColor: accentColor }}>
            <button
              onClick={() => setViewMode('week')}
              className="px-4 py-2 text-sm font-semibold transition-all"
              style={{
                backgroundColor: viewMode === 'week' ? accentColor : 'white',
                color: viewMode === 'week' ? 'white' : accentColor,
              }}
              data-testid="goods-in-week-toggle"
            >
              Week
            </button>
            <button
              onClick={() => setViewMode('month')}
              className="px-4 py-2 text-sm font-semibold transition-all"
              style={{
                backgroundColor: viewMode === 'month' ? accentColor : 'white',
                color: viewMode === 'month' ? 'white' : accentColor,
              }}
              data-testid="goods-in-month-toggle"
            >
              Month
            </button>
          </div>
          {/* Add Entry button */}
          <button
            onClick={openAddModal}
            className="flex items-center justify-center gap-2 px-4 py-2 rounded-xl border-2 font-semibold text-sm transition-all hover:shadow-md"
            style={{ borderColor: accentColor, color: accentColor, backgroundColor: 'white' }}
            data-testid="goods-in-add-btn"
          >
            <Plus className="w-4 h-4" />
            Add Entry
          </button>
        </div>

        {/* Week View - Title on left side */}
        {viewMode === 'week' && (
          <div className="flex">
            {/* Vertical Title - rounded corners face outward (toward page edge) */}
            {/* Note: Using rounded-r because element is rotated 180deg */}
            <div 
              className="flex items-center justify-center rounded-r-xl px-2"
              style={{ 
                writingMode: 'vertical-rl', 
                textOrientation: 'mixed', 
                transform: 'rotate(180deg)',
                backgroundColor: accentColor
              }}
            >
              <span className="text-white font-bold text-sm tracking-wider flex items-center gap-2">
                <CalendarIcon className="w-4 h-4" />
                WEEK VIEW
              </span>
            </div>
            
            {/* Week Content - rounded corners on right edge */}
            <div className="flex-1 bg-gray-50 rounded-r-xl p-3">
              <div className="grid grid-cols-5 gap-3">
                {workingWeekDates.map((date, idx) => {
                  // Filter out weekend deliveries for week view
                  const dayDeliveries = getDeliveriesForDate(date).filter(d => !isWeekend(new Date(d.date)));
                  const dayTransfers = getTransfersForDate(date);
                  const isToday = isSameDay(date, today);
                  const dayNum = date.getDate();
                  
                  // Determine if this day is in a different week than today
                  const todayWeekStart = startOfWeek(today, { weekStartsOn: 1 });
                  const dateWeekStart = startOfWeek(date, { weekStartsOn: 1 });
                  const isNextWeek = dateWeekStart > todayWeekStart;
                  
                  // Header color: accent color for today, light grey for next week, dark grey for current week
                  const headerColor = isToday ? accentColor : (isNextWeek ? '#d1d5db' : '#6b7280');
                  const headerTextColor = isToday ? 'white' : (isNextWeek ? '#4b5563' : 'white');
                  
                  // Group transfers by branch for this day
                  const groupedDayTransfers = groupTransfersByBranch(dayTransfers);
                  
                  return (
                    <div
                      key={idx}
                      className="bg-white rounded-xl shadow-md overflow-hidden flex flex-col"
                      style={{ 
                        minHeight: `${Math.max(160, 50 + (maxDeliveriesInWeek + Object.keys(groupedDayTransfers).length) * 70)}px`
                      }}
                    >
                      {/* Day Header - Single line with day and date */}
                      <div 
                        className="px-3 py-2 text-center font-bold text-sm"
                        style={{ 
                          backgroundColor: headerColor,
                          color: headerTextColor
                        }}
                      >
                        {format(date, 'EEE')} {dayNum}{getOrdinalSuffix(dayNum)} {format(date, 'MMM')}
                      </div>
                      
                      {/* Deliveries & Transfers List */}
                      <div className="p-2 space-y-1.5 flex-1 overflow-y-auto">
                        {/* Transfer cards - Slimmer style grouped by branch + direction */}
                        {Object.entries(groupedDayTransfers).map(([groupKey, group]) => {
                          const { branchName, direction, transfers: branchTransfers } = group;
                          // Card color based on direction (all in group have same direction)
                          const cardColor = branchTransfers.every(t => t.status === 'complete') ? '#22c55e' : 
                                           branchTransfers.some(t => t.status === 'failed') ? '#ef4444' : 
                                           direction === 'out' ? enconPink : '#3b82f6';
                          const bgColor = branchTransfers.every(t => t.status === 'complete') ? 'rgba(34, 197, 94, 0.1)' :
                                         branchTransfers.some(t => t.status === 'failed') ? 'rgba(239, 68, 68, 0.1)' :
                                         direction === 'out' ? 'rgba(236, 72, 153, 0.08)' : 'rgba(59, 130, 246, 0.08)';
                          
                          return (
                            <div
                              key={`transfer-${groupKey}`}
                              onClick={() => branchTransfers.length > 1 ? setExpandedTransferGroup({ name: branchName, transfers: branchTransfers }) : openEditModal(branchTransfers[0])}
                              className="px-2 py-1.5 rounded-md border-l-3 cursor-pointer hover:shadow-sm transition-all"
                              style={{
                                borderLeftWidth: '3px',
                                borderLeftColor: cardColor,
                                backgroundColor: bgColor
                              }}
                            >
                              <div className="flex items-center gap-1">
                                <ArrowRightLeft className="w-3 h-3 flex-shrink-0" style={{ color: cardColor }} />
                                <span className="text-[10px] font-semibold truncate flex-1" style={{ color: cardColor }}>{branchName}</span>
                                {/* Creator name inline */}
                                {branchTransfers.length === 1 && branchTransfers[0].created_by && (
                                  <span className="text-[9px] text-gray-400 flex-shrink-0">
                                    {formatCreatorName(branchTransfers[0].created_by)}
                                  </span>
                                )}
                                {/* Stock IN/OUT Tag inline */}
                                {!branchTransfers.every(t => t.status === 'complete') && !branchTransfers.some(t => t.status === 'failed') && (
                                  <span 
                                    className="text-[8px] font-bold px-1 py-0.5 rounded text-white flex-shrink-0"
                                    style={{ backgroundColor: direction === 'out' ? enconPink : '#3b82f6' }}
                                  >
                                    {direction === 'out' ? 'OUT' : 'IN'}
                                  </span>
                                )}
                                {branchTransfers.length > 1 && (
                                  <span 
                                    className="text-[9px] text-white px-1 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: cardColor }}
                                  >
                                    {branchTransfers.length}
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                        
                        {/* Regular delivery cards */}
                        {dayDeliveries.length === 0 && Object.keys(groupedDayTransfers).length === 0 ? (
                          <div className="text-xs text-gray-400 text-center py-4">
                            No deliveries
                          </div>
                        ) : (
                          dayDeliveries.map((delivery) => renderDeliveryCard(delivery, 'small'))
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
        
        {/* Month View */}
        {viewMode === 'month' && (
          <div className="bg-white rounded-xl shadow-md p-4">
            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => setCalendarMonth(subMonths(calendarMonth, 1))}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <ChevronLeft className="w-6 h-6" style={{ color: accentColor }} />
              </button>
              
              <h3 className="text-xl font-bold" style={{ color: enconBlack }}>
                {format(calendarMonth, 'MMMM yyyy')}
              </h3>
              
              <button
                onClick={() => setCalendarMonth(addMonths(calendarMonth, 1))}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <ChevronRight className="w-6 h-6" style={{ color: accentColor }} />
              </button>
            </div>
            
            {/* Calendar Grid */}
            {(() => {
              const calendarDays = getCalendarDays(calendarMonth);
              const weeks = [];
              for (let i = 0; i < calendarDays.length; i += 7) {
                weeks.push(calendarDays.slice(i, i + 7));
              }
              
              return (
                <div className="space-y-2">
                  {/* Day Headers */}
                  <div className="grid grid-cols-7 gap-2">
                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                      <div key={day} className="text-center text-sm font-semibold py-2" style={{ color: accentColor }}>
                        {day}
                      </div>
                    ))}
                  </div>
                  
                  {/* Calendar Weeks */}
                  {weeks.map((week, weekIdx) => {
                    const maxInWeek = Math.max(...week.map(date => getDeliveriesForDate(date).length), 1);
                    const rowMinHeight = Math.max(80, 40 + maxInWeek * 30);
                    
                    return (
                      <div key={weekIdx} className="grid grid-cols-7 gap-2" style={{ minHeight: `${rowMinHeight}px` }}>
                        {week.map((date, dayIdx) => {
                          const dayDeliveries = getDeliveriesForDate(date);
                          const isCurrentMonth = isSameMonth(date, calendarMonth);
                          const isWeekendDay = isWeekend(date);
                          const isTodayDate = isSameDay(date, today);
                          
                          return (
                            <div
                              key={dayIdx}
                              onClick={() => dayDeliveries.length > 0 && openDayModal(date, dayDeliveries)}
                              className={`rounded-lg p-1 cursor-pointer hover:bg-gray-100 transition-colors ${
                                isWeekendDay ? 'bg-gray-100' : 'bg-gray-50'
                              } ${!isCurrentMonth ? 'opacity-40' : ''} ${
                                isTodayDate ? 'ring-2 ring-orange-400' : ''
                              }`}
                            >
                              <div className={`text-xs font-semibold mb-1 ${
                                isTodayDate ? 'text-orange-600' : 'text-gray-600'
                              }`}>
                                {format(date, 'd')}
                              </div>
                              <div className="space-y-0.5 overflow-y-auto max-h-[80px]">
                                {dayDeliveries.slice(0, 3).map((delivery) => (
                                  <div
                                    key={delivery.id}
                                    onClick={(e) => { e.stopPropagation(); openEditModal(delivery); }}
                                    className="text-[10px] px-1 py-0.5 rounded cursor-pointer truncate hover:opacity-80"
                                    style={{
                                      backgroundColor: delivery.status === 'complete' ? 'rgba(34, 197, 94, 0.2)' :
                                                      delivery.status === 'failed' ? 'rgba(239, 68, 68, 0.2)' :
                                                      'rgba(238, 114, 3, 0.2)',
                                      color: getStatusColor(delivery.status)
                                    }}
                                  >
                                    {delivery.supplier_name}
                                  </div>
                                ))}
                                {dayDeliveries.length > 3 && (
                                  <div 
                                    className="text-[9px] text-blue-500 text-center font-medium hover:text-blue-700 cursor-pointer"
                                  >
                                    +{dayDeliveries.length - 3} more
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* Add Entry Modal */}
      <Dialog open={showAddModal} onOpenChange={(open) => { setShowAddModal(open); if (!open) { setBranchSearch(''); setShowBranchDropdown(false); } }}>
        <DialogContent 
          className="bg-white shadow-2xl p-0 overflow-hidden"
          style={{
            borderRadius: '24px',
            border: '3px solid transparent',
            background: `linear-gradient(white, white) padding-box, linear-gradient(135deg, ${enconOrange}, ${enconPurple}) border-box`,
            maxWidth: isTransferMode ? '600px' : '520px',
            width: '95vw'
          }}
        >
          <DialogHeader className="px-6 pt-6 pb-4">
            <DialogTitle className="text-xl font-bold flex items-center gap-3" style={{ color: enconBlack }}>
              {isTransferMode ? (
                <div className="p-2 rounded-xl" style={{ backgroundColor: formEntry.stock_direction === 'out' ? 'rgba(236, 72, 153, 0.15)' : 'rgba(59, 130, 246, 0.15)' }}>
                  <ArrowRightLeft className="w-6 h-6" style={{ color: formEntry.stock_direction === 'out' ? enconPink : '#3B82F6' }} />
                </div>
              ) : (
                <div className="p-2 rounded-xl" style={{ backgroundColor: 'rgba(238, 114, 3, 0.15)' }}>
                  <Plus className="w-6 h-6" style={{ color: enconOrange }} />
                </div>
              )}
              {isTransferMode ? 'Add Stock Transfer' : 'Add Delivery Entry'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="px-6 pb-6 space-y-5">
            {/* Transfer Mode Toggle - Pill style */}
            <div 
              className="flex items-center justify-between p-4 transition-all"
              style={{ 
                backgroundColor: isTransferMode ? 'rgba(59, 130, 246, 0.08)' : 'rgba(238, 114, 3, 0.08)',
                borderRadius: '16px',
                minHeight: '56px'
              }}
            >
              <div className="flex items-center gap-3">
                <ArrowRightLeft className="w-5 h-5" style={{ color: isTransferMode ? '#3B82F6' : enconOrange }} />
                <span className="font-semibold">Toggle Transfer</span>
              </div>
              <Switch
                checked={isTransferMode}
                onCheckedChange={setIsTransferMode}
                className="data-[state=checked]:bg-blue-600"
              />
            </div>

            {/* Stock Movement Direction - Only show when Transfer Mode is ON */}
            {isTransferMode && (
              <div 
                className="flex items-center justify-between p-4 transition-all"
                style={{ 
                  backgroundColor: formEntry.stock_direction === 'out' ? 'rgba(236, 72, 153, 0.1)' : 'rgba(59, 130, 246, 0.1)',
                  borderRadius: '16px',
                  minHeight: '56px'
                }}
              >
                <div className="flex items-center gap-3">
                  <span className="font-semibold" style={{ color: formEntry.stock_direction === 'out' ? enconPink : '#3B82F6' }}>
                    Stock Movement
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-sm font-bold ${formEntry.stock_direction === 'in' ? 'text-blue-600' : 'text-gray-400'}`}>
                    IN
                  </span>
                  <Switch
                    checked={formEntry.stock_direction === 'out'}
                    onCheckedChange={(checked) => setFormEntry({ ...formEntry, stock_direction: checked ? 'out' : 'in' })}
                    className="data-[state=checked]:bg-pink-500 data-[state=unchecked]:bg-blue-500"
                  />
                  <span className={`text-sm font-bold ${formEntry.stock_direction === 'out' ? 'text-pink-500' : 'text-gray-400'}`}>
                    OUT
                  </span>
                </div>
              </div>
            )}

            {isTransferMode ? (
              <>
                {/* Two column layout for transfer mode */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Branch Name (Transfer Mode) - Searchable Dropdown */}
                  <div className="col-span-2">
                    <label className="block text-sm font-semibold mb-2" style={{ color: enconBlack }}>
                      Branch Name
                    </label>
                    <div className="relative">
                      <div 
                        className="w-full px-4 py-3 flex items-center justify-between cursor-pointer transition-all"
                        style={{
                          backgroundColor: '#1f2937',
                          borderRadius: '14px',
                          border: showBranchDropdown ? '2px solid #3B82F6' : '2px solid transparent'
                        }}
                        onClick={() => setShowBranchDropdown(!showBranchDropdown)}
                      >
                        <input
                          type="text"
                          placeholder={formEntry.branch_id ? '' : "Search branch..."}
                          value={showBranchDropdown ? branchSearch : (formEntry.branch_id ? (() => { const b = BRANCHES.find(br => br.id === formEntry.branch_id); return b ? `${b.name} (${b.alternateName})` : ''; })() : '')}
                          onChange={(e) => { setBranchSearch(e.target.value); if (!showBranchDropdown) setShowBranchDropdown(true); }}
                          onClick={(e) => { e.stopPropagation(); setShowBranchDropdown(true); }}
                          className="flex-1 bg-transparent text-white placeholder-gray-400 focus:outline-none"
                        />
                        <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showBranchDropdown ? 'rotate-180' : ''}`} />
                      </div>
                      
                      {/* Dropdown List */}
                      {showBranchDropdown && (
                        <div 
                          className="absolute z-50 w-full mt-2 max-h-64 overflow-y-auto shadow-xl"
                          style={{
                            backgroundColor: '#1f2937',
                            borderRadius: '14px',
                            border: '1px solid rgba(255,255,255,0.1)'
                          }}
                        >
                          {BRANCHES
                            .filter(branch => {
                              const search = branchSearch.toLowerCase();
                              return !search || 
                                branch.name.toLowerCase().includes(search) || 
                                branch.alternateName.toLowerCase().includes(search) ||
                                branch.id.toLowerCase().includes(search);
                            })
                            .map(branch => (
                              <div
                                key={branch.id}
                                onClick={() => {
                                  setFormEntry({ ...formEntry, branch_id: branch.id });
                                  setBranchSearch('');
                                  setShowBranchDropdown(false);
                                }}
                                className="px-4 py-3 cursor-pointer transition-all hover:bg-white/10 flex items-center justify-between"
                                style={{
                                  backgroundColor: formEntry.branch_id === branch.id ? 'rgba(59, 130, 246, 0.2)' : 'transparent'
                                }}
                              >
                                <div className="flex items-center gap-2">
                                  <Building2 className="w-4 h-4 text-blue-400" />
                                  <span className="text-white font-medium">{branch.name}</span>
                                  <span className="text-gray-400 text-sm">({branch.alternateName})</span>
                                </div>
                                {formEntry.branch_id === branch.id && (
                                  <Check className="w-4 h-4 text-blue-400" />
                                )}
                              </div>
                            ))
                          }
                          {BRANCHES.filter(branch => {
                            const search = branchSearch.toLowerCase();
                            return !search || 
                              branch.name.toLowerCase().includes(search) || 
                              branch.alternateName.toLowerCase().includes(search);
                          }).length === 0 && (
                            <div className="px-4 py-3 text-gray-400 text-center">No branches found</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Date */}
                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: enconBlack }}>
                      Expected Date
                    </label>
                    <Input
                      type="date"
                      value={formEntry.date}
                      onChange={(e) => {
                        const selectedDate = new Date(e.target.value);
                        if (isWeekend(selectedDate)) {
                          toast.error('Please select a weekday');
                          return;
                        }
                        setFormEntry({ ...formEntry, date: e.target.value });
                      }}
                      className="w-full px-4 py-3 border-2 focus:border-blue-400 focus:ring-blue-400"
                      style={{ borderRadius: '14px' }}
                    />
                  </div>

                  {/* Expense Number */}
                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: enconBlack }}>
                      Expense Number
                    </label>
                    <Input
                      type="text"
                      placeholder="##X"
                      value={formEntry.order_number}
                      onChange={(e) => setFormEntry({ ...formEntry, order_number: e.target.value })}
                      className="w-full px-4 py-3 border-2 focus:border-blue-400 focus:ring-blue-400"
                      style={{ borderRadius: '14px' }}
                    />
                  </div>

                  {/* Carrier / Collection Details - Full width */}
                  <div className="col-span-2">
                    <label className="block text-sm font-semibold mb-2" style={{ color: enconBlack }}>
                      Carrier / Collection Details
                    </label>
                    <Input
                      type="text"
                      placeholder="Enter carrier or collection details"
                      value={formEntry.carrier_details}
                      onChange={(e) => setFormEntry({ ...formEntry, carrier_details: e.target.value })}
                      className="w-full px-4 py-3 border-2 focus:border-blue-400 focus:ring-blue-400"
                      style={{ borderRadius: '14px' }}
                    />
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Regular delivery mode - Two column layout */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Date Field - Full width */}
                  <div className="col-span-2">
                    <label className="block text-sm font-semibold mb-2" style={{ color: enconBlack }}>
                      Date <span className="text-gray-400 font-normal">(Weekdays only)</span>
                    </label>
                    <Input
                      type="date"
                      value={formEntry.date}
                      onChange={(e) => {
                        const selectedDate = new Date(e.target.value);
                        if (isWeekend(selectedDate)) {
                          toast.error('Please select a weekday');
                          return;
                        }
                        setFormEntry({ ...formEntry, date: e.target.value });
                      }}
                      className="w-full px-4 py-3 border-2 focus:border-orange-400 focus:ring-orange-400"
                      style={{ borderRadius: '14px' }}
                    />
                  </div>

                  {/* Supplier Name - Side by side */}
                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: enconBlack }}>
                      Supplier Name
                    </label>
                    <Input
                      type="text"
                      placeholder="Enter supplier name"
                      value={formEntry.supplier_name}
                      onChange={(e) => setFormEntry({ ...formEntry, supplier_name: e.target.value })}
                      className="w-full px-4 py-3 border-2 focus:border-orange-400 focus:ring-orange-400"
                      style={{ borderRadius: '14px' }}
                    />
                  </div>

                  {/* Order Number - Side by side */}
                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: enconBlack }}>
                      Order Number
                    </label>
                    <Input
                      type="text"
                      placeholder="Enter order number"
                      value={formEntry.order_number}
                      onChange={(e) => setFormEntry({ ...formEntry, order_number: e.target.value })}
                      className="w-full px-4 py-3 border-2 focus:border-orange-400 focus:ring-orange-400"
                      style={{ borderRadius: '14px' }}
                    />
                  </div>
                </div>
              </>
            )}

            {/* Notes (Both modes) */}
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: enconBlack }}>
                Notes (optional)
              </label>
              <textarea
                placeholder="Add any notes..."
                value={formEntry.notes}
                onChange={(e) => setFormEntry({ ...formEntry, notes: e.target.value })}
                rows={2}
                className="w-full px-4 py-3 border-2 focus:border-orange-400 focus:ring-orange-400 focus:outline-none resize-none"
                style={{ borderRadius: '14px' }}
              />
            </div>

            {/* Buttons - More rounded with fresh styling */}
            <div className="flex gap-4 pt-2">
              <Button
                variant="outline"
                onClick={() => { setShowAddModal(false); setBranchSearch(''); setShowBranchDropdown(false); }}
                className="flex-1 py-3 font-semibold border-2 hover:bg-gray-100 transition-all"
                style={{ borderRadius: '14px' }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddDelivery}
                className="flex-1 py-3 text-white font-semibold transition-all hover:opacity-90"
                style={{ 
                  backgroundColor: isTransferMode ? (formEntry.stock_direction === 'out' ? enconPink : '#3B82F6') : enconOrange,
                  borderRadius: '14px'
                }}
              >
                {isTransferMode ? `Add Stock ${formEntry.stock_direction === 'out' ? 'Out' : 'In'}` : 'Add Entry'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Entry Modal */}
      <Dialog open={showEditModal} onOpenChange={(open) => { setShowEditModal(open); if (!open) { setBranchSearch(''); setShowBranchDropdown(false); } }}>
        <DialogContent 
          className="bg-white shadow-2xl p-0 overflow-hidden"
          style={{ 
            borderRadius: '24px',
            border: '3px solid transparent',
            background: `linear-gradient(white, white) padding-box, linear-gradient(135deg, ${enconOrange}, ${enconPurple}) border-box`,
            maxWidth: editingEntry?.purchase_order_data ? '1100px' : (isTransferMode ? '600px' : '520px'),
            width: '95vw',
          }}
        >
          <DialogHeader className="px-6 pt-6 pb-4 pr-14 relative">
            <DialogTitle className="text-xl font-bold flex items-center gap-3" style={{ color: enconBlack }}>
              {isTransferMode ? (
                <div className="p-2 rounded-xl" style={{ backgroundColor: formEntry.stock_direction === 'out' ? 'rgba(236, 72, 153, 0.15)' : 'rgba(59, 130, 246, 0.15)' }}>
                  <ArrowRightLeft className="w-6 h-6" style={{ color: formEntry.stock_direction === 'out' ? enconPink : '#3B82F6' }} />
                </div>
              ) : (
                <div className="p-2 rounded-xl" style={{ backgroundColor: 'rgba(238, 114, 3, 0.15)' }}>
                  <Package className="w-6 h-6" style={{ color: enconOrange }} />
                </div>
              )}
              {isTransferMode ? 'Edit Transfer' : 'Edit Delivery'}
            </DialogTitle>
          </DialogHeader>
          
          <div className={editingEntry?.purchase_order_data ? 'flex' : 'px-6 pb-6 space-y-5'}>
            {/* ── Left column: form ────────────────────────────────────────── */}
            <div
              className="px-6 pt-3 pb-6 space-y-5 overflow-y-auto flex-shrink-0"
              style={editingEntry?.purchase_order_data ? { width: '480px', maxHeight: '82vh' } : {}}
            >
            {/* Creator info */}
            {editingEntry?.created_by && (
              <div className="text-sm text-gray-500">
                Added by: <span className="font-semibold">{formatCreatorName(editingEntry.created_by)}</span>
              </div>
            )}

            {/* Toggle Transfer */}
            <div 
              className="flex items-center justify-between p-4 transition-all"
              style={{ 
                backgroundColor: isTransferMode ? 'rgba(59, 130, 246, 0.08)' : 'rgba(238, 114, 3, 0.08)',
                borderRadius: '16px',
                minHeight: '56px'
              }}
            >
              <div className="flex items-center gap-3">
                <ArrowRightLeft className="w-5 h-5" style={{ color: isTransferMode ? '#3B82F6' : enconOrange }} />
                <span className="font-semibold">Toggle Transfer</span>
              </div>
              <Switch
                checked={isTransferMode}
                onCheckedChange={(checked) => {
                  setIsTransferMode(checked);
                  setFormEntry({ ...formEntry, is_transfer: checked });
                }}
                className="data-[state=checked]:bg-blue-600"
              />
            </div>

            {/* Stock Movement Direction - Only show when Transfer Mode is ON */}
            {isTransferMode && (
              <div 
                className="flex items-center justify-between p-4 transition-all"
                style={{ 
                  backgroundColor: formEntry.stock_direction === 'out' ? 'rgba(236, 72, 153, 0.1)' : 'rgba(59, 130, 246, 0.1)',
                  borderRadius: '16px',
                  minHeight: '56px'
                }}
              >
                <div className="flex items-center gap-3">
                  <span className="font-semibold" style={{ color: formEntry.stock_direction === 'out' ? enconPink : '#3B82F6' }}>
                    Stock Movement
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-sm font-bold ${formEntry.stock_direction === 'in' ? 'text-blue-600' : 'text-gray-400'}`}>
                    IN
                  </span>
                  <Switch
                    checked={formEntry.stock_direction === 'out'}
                    onCheckedChange={(checked) => setFormEntry({ ...formEntry, stock_direction: checked ? 'out' : 'in' })}
                    className="data-[state=checked]:bg-pink-500 data-[state=unchecked]:bg-blue-500"
                  />
                  <span className={`text-sm font-bold ${formEntry.stock_direction === 'out' ? 'text-pink-500' : 'text-gray-400'}`}>
                    OUT
                  </span>
                </div>
              </div>
            )}

            {isTransferMode ? (
              <>
                {/* Two column layout for transfer mode */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Branch Name (Transfer Mode) - Searchable Dropdown */}
                  <div className="col-span-2">
                    <label className="block text-sm font-semibold mb-2" style={{ color: enconBlack }}>
                      Branch Name
                    </label>
                    <div className="relative">
                      <div 
                        className="w-full px-4 py-3 flex items-center justify-between cursor-pointer transition-all"
                        style={{
                          backgroundColor: '#1f2937',
                          borderRadius: '14px',
                          border: showBranchDropdown ? '2px solid #3B82F6' : '2px solid transparent'
                        }}
                        onClick={() => setShowBranchDropdown(!showBranchDropdown)}
                      >
                        <input
                          type="text"
                          placeholder={formEntry.branch_id ? '' : "Search branch..."}
                          value={showBranchDropdown ? branchSearch : (formEntry.branch_id ? (() => { const b = BRANCHES.find(br => br.id === formEntry.branch_id); return b ? `${b.name} (${b.alternateName})` : ''; })() : '')}
                          onChange={(e) => { setBranchSearch(e.target.value); if (!showBranchDropdown) setShowBranchDropdown(true); }}
                          onClick={(e) => { e.stopPropagation(); setShowBranchDropdown(true); }}
                          className="flex-1 bg-transparent text-white placeholder-gray-400 focus:outline-none"
                        />
                        <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showBranchDropdown ? 'rotate-180' : ''}`} />
                      </div>
                      
                      {/* Dropdown List */}
                      {showBranchDropdown && (
                        <div 
                          className="absolute z-50 w-full mt-2 max-h-64 overflow-y-auto shadow-xl"
                          style={{
                            backgroundColor: '#1f2937',
                            borderRadius: '14px',
                            border: '1px solid rgba(255,255,255,0.1)'
                          }}
                        >
                          {BRANCHES
                            .filter(branch => {
                              const search = branchSearch.toLowerCase();
                              return !search || 
                                branch.name.toLowerCase().includes(search) || 
                                branch.alternateName.toLowerCase().includes(search) ||
                                branch.id.toLowerCase().includes(search);
                            })
                            .map(branch => (
                              <div
                                key={branch.id}
                                onClick={() => {
                                  setFormEntry({ ...formEntry, branch_id: branch.id, supplier_name: branch.name });
                                  setBranchSearch('');
                                  setShowBranchDropdown(false);
                                }}
                                className="px-4 py-3 cursor-pointer transition-all hover:bg-white/10 flex items-center justify-between"
                                style={{
                                  backgroundColor: formEntry.branch_id === branch.id ? 'rgba(59, 130, 246, 0.2)' : 'transparent'
                                }}
                              >
                                <div className="flex items-center gap-2">
                                  <Building2 className="w-4 h-4 text-blue-400" />
                                  <span className="text-white font-medium">{branch.name}</span>
                                  <span className="text-gray-400 text-sm">({branch.alternateName})</span>
                                </div>
                                {formEntry.branch_id === branch.id && (
                                  <Check className="w-4 h-4 text-blue-400" />
                                )}
                              </div>
                            ))
                          }
                          {BRANCHES.filter(branch => {
                            const search = branchSearch.toLowerCase();
                            return !search || 
                              branch.name.toLowerCase().includes(search) || 
                              branch.alternateName.toLowerCase().includes(search);
                          }).length === 0 && (
                            <div className="px-4 py-3 text-gray-400 text-center">No branches found</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Date */}
                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: enconBlack }}>
                      Expected Date
                    </label>
                    <Input
                      type="date"
                      value={formEntry.date}
                      onChange={(e) => {
                        const selectedDate = new Date(e.target.value);
                        if (isWeekend(selectedDate)) {
                          toast.error('Please select a weekday');
                          return;
                        }
                        setFormEntry({ ...formEntry, date: e.target.value });
                      }}
                      className="w-full px-4 py-3 border-2 focus:border-blue-400 focus:ring-blue-400"
                      style={{ borderRadius: '14px' }}
                    />
                  </div>

                  {/* Expense Number */}
                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: enconBlack }}>
                      Expense Number
                    </label>
                    <Input
                      type="text"
                      placeholder="##X"
                      value={formEntry.order_number}
                      onChange={(e) => setFormEntry({ ...formEntry, order_number: e.target.value })}
                      className="w-full px-4 py-3 border-2 focus:border-blue-400 focus:ring-blue-400"
                      style={{ borderRadius: '14px' }}
                    />
                  </div>

                  {/* Carrier / Collection Details - Full width */}
                  <div className="col-span-2">
                    <label className="block text-sm font-semibold mb-2" style={{ color: enconBlack }}>
                      Carrier / Collection Details
                    </label>
                    <Input
                      type="text"
                      placeholder="Enter carrier or collection details"
                      value={formEntry.carrier_details}
                      onChange={(e) => setFormEntry({ ...formEntry, carrier_details: e.target.value })}
                      className="w-full px-4 py-3 border-2 focus:border-blue-400 focus:ring-blue-400"
                      style={{ borderRadius: '14px' }}
                    />
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Regular delivery mode - Two column layout */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Date Field - Full width */}
                  <div className="col-span-2">
                    <label className="block text-sm font-semibold mb-2" style={{ color: enconBlack }}>
                      Date <span className="text-gray-400 font-normal">(Weekdays only)</span>
                    </label>
                    <Input
                      type="date"
                      value={formEntry.date}
                      onChange={(e) => {
                        const selectedDate = new Date(e.target.value);
                        if (isWeekend(selectedDate)) {
                          toast.error('Please select a weekday');
                          return;
                        }
                        setFormEntry({ ...formEntry, date: e.target.value });
                      }}
                      className="w-full px-4 py-3 border-2 focus:border-orange-400 focus:ring-orange-400"
                      style={{ borderRadius: '14px' }}
                    />
                  </div>

                  {/* Supplier Name - Side by side */}
                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: enconBlack }}>
                      Supplier Name
                    </label>
                    <Input
                      type="text"
                      placeholder="Enter supplier name"
                      value={formEntry.supplier_name}
                      onChange={(e) => setFormEntry({ ...formEntry, supplier_name: e.target.value })}
                      className="w-full px-4 py-3 border-2 focus:border-orange-400 focus:ring-orange-400"
                      style={{ borderRadius: '14px' }}
                    />
                  </div>

                  {/* Order Number - Side by side */}
                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: enconBlack }}>
                      Order Number
                    </label>
                    <Input
                      type="text"
                      placeholder="Enter order number"
                      value={formEntry.order_number}
                      onChange={(e) => setFormEntry({ ...formEntry, order_number: e.target.value })}
                      className="w-full px-4 py-3 border-2 focus:border-orange-400 focus:ring-orange-400"
                      style={{ borderRadius: '14px' }}
                    />
                  </div>
                </div>
              </>
            )}

            {/* Notes (Both modes) */}
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: enconBlack }}>
                Notes (optional)
              </label>
              <textarea
                placeholder="Add any notes..."
                value={formEntry.notes}
                onChange={(e) => setFormEntry({ ...formEntry, notes: e.target.value })}
                rows={2}
                className="w-full px-4 py-3 border-2 focus:border-orange-400 focus:ring-orange-400 focus:outline-none resize-none"
                style={{ borderRadius: '14px' }}
              />
            </div>

            {/* Status indicator */}
            {editingEntry?.status && editingEntry.status !== 'pending' && (
              <div className={`p-3 text-sm font-semibold text-center ${
                editingEntry.status === 'complete' ? 'bg-green-100 text-green-700' :
                editingEntry.status === 'failed' ? 'bg-red-100 text-red-700' : ''
              }`} style={{ borderRadius: '14px' }}>
                Status: {editingEntry.status.charAt(0).toUpperCase() + editingEntry.status.slice(1)}
              </div>
            )}

            {/* Status Action Buttons */}
            <div className="grid grid-cols-3 gap-3 pt-2 border-t">
              <Button
                onClick={() => handleUpdateStatus('complete')}
                disabled={editingEntry?.status === 'complete'}
                className={`py-3 font-semibold ${editingEntry?.status === 'complete' ? 'opacity-50' : ''}`}
                style={{ 
                  backgroundColor: editingEntry?.status === 'complete' ? '#86efac' : '#22c55e',
                  color: 'white',
                  borderRadius: '14px'
                }}
              >
                <Check className="w-4 h-4 mr-1" />
                Complete
              </Button>
              <Button
                onClick={() => handleUpdateStatus('failed')}
                disabled={editingEntry?.status === 'failed'}
                className={`py-3 font-semibold ${editingEntry?.status === 'failed' ? 'opacity-50' : ''}`}
                style={{ 
                  backgroundColor: editingEntry?.status === 'failed' ? '#fca5a5' : '#ef4444',
                  color: 'white',
                  borderRadius: '14px'
                }}
              >
                <XCircle className="w-4 h-4 mr-1" />
                Failed
              </Button>
              <Button
                variant="outline"
                onClick={() => handleUpdateStatus('pending')}
                disabled={!editingEntry?.status || editingEntry?.status === 'pending'}
                className={`py-3 font-semibold border-2 ${!editingEntry?.status || editingEntry?.status === 'pending' ? 'opacity-50 cursor-not-allowed border-gray-200' : 'hover:bg-gray-100 border-gray-400'}`}
                style={{ borderRadius: '14px' }}
              >
                Clear
              </Button>
            </div>

            {/* Main Action Buttons */}
            <div className="flex gap-4 pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowEditModal(false);
                  setEditingEntry(null);
                  setBranchSearch('');
                  setShowBranchDropdown(false);
                }}
                className="flex-1 py-3 font-semibold border-2 hover:bg-gray-100 transition-all"
                style={{ borderRadius: '14px' }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdateDelivery}
                className="flex-1 py-3 text-white font-semibold transition-all hover:opacity-90"
                style={{ 
                  backgroundColor: isTransferMode ? (formEntry.stock_direction === 'out' ? enconPink : '#3b82f6') : enconOrange, 
                  borderRadius: '14px' 
                }}
              >
                Save Changes
              </Button>
            </div>

            {/* Delete — bottom of form, away from save/cancel */}
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center gap-1.5 mx-auto text-xs text-red-400 hover:text-red-600 transition-colors py-1 px-3 rounded-lg hover:bg-red-50"
              data-testid="delete-delivery-btn"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Delete this entry
            </button>
            
            {/* History Log */}
            {editingEntry?.history && editingEntry.history.length > 0 && (
              <div className="mt-4 pt-3 border-t border-gray-200">
                <p className="text-[10px] text-gray-400 mb-1">History:</p>
                <div className="space-y-0.5">
                  {editingEntry.history.slice().reverse().map((entry, idx) => (
                    <p key={idx} className="text-[10px] text-gray-500">
                      {new Date(entry.timestamp).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' })} - {new Date(entry.timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} - {entry.user?.split('@')[0] || 'Unknown'} - ({entry.action})
                    </p>
                  ))}
                </div>
              </div>
            )}

            </div>{/* end left column */}

            {/* ── Right column: parsed PO details ─────────────────────────── */}
            {editingEntry?.purchase_order_data && (
              <div
                className="flex-1 min-w-[340px] px-6 pb-6 pt-2 overflow-y-auto border-l border-gray-100 bg-gray-50/30"
                style={{ maxHeight: '82vh' }}
              >
                <GoodsInPOPanel data={editingEntry.purchase_order_data} noTopBorder />
              </div>
            )}
          </div>{/* end flex wrapper */}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="bg-white max-w-sm mx-4 rounded-xl shadow-2xl">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Confirm Delete
            </DialogTitle>
          </DialogHeader>
          <p className="text-gray-600 mt-2">
            Are you sure you want to delete this delivery entry? This action cannot be undone.
          </p>
          <div className="flex gap-3 mt-4">
            <Button
              variant="outline"
              onClick={() => setShowDeleteConfirm(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteDelivery}
              className="flex-1 bg-red-500 hover:bg-red-600 text-white"
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Expanded Transfer Group Modal */}
      <Dialog open={!!expandedTransferGroup} onOpenChange={() => setExpandedTransferGroup(null)}>
        <DialogContent className="bg-white max-w-md mx-4 rounded-xl shadow-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2" style={{ color: '#3b82f6' }}>
              <ArrowRightLeft className="w-6 h-6" />
              {expandedTransferGroup?.name}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-500 -mt-2">
            {expandedTransferGroup?.transfers?.length} transfers from this branch
          </p>
          <div className="space-y-2 mt-4 max-h-[400px] overflow-y-auto">
            {expandedTransferGroup?.transfers?.map((transfer) => (
              <div
                key={transfer.id}
                onClick={() => {
                  setExpandedTransferGroup(null);
                  openEditModal(transfer);
                }}
                className="p-3 rounded-lg border-l-4 cursor-pointer hover:shadow-md transition-all"
                style={{
                  borderLeftColor: transfer.status === 'complete' ? '#22c55e' : 
                                  transfer.status === 'failed' ? '#ef4444' : '#3b82f6',
                  backgroundColor: transfer.status === 'complete' ? 'rgba(34, 197, 94, 0.1)' :
                                  transfer.status === 'failed' ? 'rgba(239, 68, 68, 0.1)' :
                                  'rgba(59, 130, 246, 0.08)'
                }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {transfer.status === 'complete' && <Check className="w-4 h-4 text-green-500" />}
                    {transfer.status === 'failed' && <XCircle className="w-4 h-4 text-red-500" />}
                    <span className="font-semibold text-sm">#{transfer.order_number}</span>
                  </div>
                  <span className="text-xs text-gray-500">
                    {format(new Date(transfer.date), 'EEE dd/MM')}
                  </span>
                </div>
                {transfer.carrier_details && (
                  <div className="text-xs text-gray-500 mt-1 truncate">
                    {transfer.carrier_details}
                  </div>
                )}
                {transfer.notes && (
                  <div className="text-xs text-gray-400 mt-1 italic truncate">
                    {transfer.notes.split('\n')[0]}
                  </div>
                )}
              </div>
            ))}
          </div>
          <Button
            variant="outline"
            onClick={() => setExpandedTransferGroup(null)}
            className="w-full mt-4"
          >
            Close
          </Button>
        </DialogContent>
      </Dialog>

      {/* Day Entries Modal - Shows all entries for a selected day */}
      <Dialog open={showDayModal} onOpenChange={setShowDayModal}>
        <DialogContent 
          className="bg-white shadow-2xl p-0 overflow-hidden max-h-[85vh]"
          style={{
            borderRadius: '24px',
            border: '3px solid transparent',
            background: `linear-gradient(white, white) padding-box, linear-gradient(135deg, ${accentColor}, #6366f1) border-box`,
            maxWidth: '600px',
            width: '95vw'
          }}
        >
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <DialogTitle className="text-xl font-bold flex items-center gap-3" style={{ color: enconBlack }}>
              <div className="p-2 rounded-xl" style={{ backgroundColor: `${accentColor}15` }}>
                <CalendarIcon className="w-6 h-6" style={{ color: accentColor }} />
              </div>
              <div>
                <div>{selectedDayDate ? format(selectedDayDate, 'EEEE, d MMMM yyyy') : ''}</div>
                <div className="text-sm font-normal text-gray-500">
                  {selectedDayEntries.length} {selectedDayEntries.length === 1 ? 'entry' : 'entries'}
                </div>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          <div className="px-6 py-4 space-y-3 overflow-y-auto max-h-[60vh]">
            {selectedDayEntries.map((entry) => {
              const statusColor = getStatusColor(entry.status);
              const isComplete = entry.status === 'complete';
              const isFailed = entry.status === 'failed';
              const isTransfer = entry.is_transfer;
              const creatorName = formatCreatorName(entry.created_by);
              
              return (
                <div
                  key={entry.id}
                  onClick={() => {
                    setShowDayModal(false);
                    openEditModal(entry);
                  }}
                  className="p-4 rounded-xl border-l-4 bg-gray-50 hover:bg-gray-100 cursor-pointer transition-all"
                  style={{ borderLeftColor: statusColor }}
                >
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {isTransfer ? (
                          <ArrowRightLeft className="w-4 h-4" style={{ color: entry.stock_direction === 'out' ? enconPink : '#3B82F6' }} />
                        ) : (
                          <Package className="w-4 h-4" style={{ color: accentColor }} />
                        )}
                        <span className="font-bold text-gray-900 truncate">
                          {entry.supplier_name}
                        </span>
                      </div>
                      {entry.order_number && (
                        <div className="text-sm text-gray-600 mb-1">
                          Order: {entry.order_number}
                        </div>
                      )}
                      {entry.notes && (
                        <div className="text-sm text-gray-500 truncate">
                          {entry.notes}
                        </div>
                      )}
                      {creatorName && (
                        <div className="text-xs text-gray-400 mt-1">
                          Added by {creatorName}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span 
                        className="px-2 py-1 rounded-full text-xs font-medium text-white"
                        style={{ backgroundColor: statusColor }}
                      >
                        {isComplete ? 'Complete' : isFailed ? 'Failed' : 'Pending'}
                      </span>
                      {isTransfer && (
                        <span 
                          className="px-2 py-0.5 rounded text-xs font-medium"
                          style={{ 
                            backgroundColor: entry.stock_direction === 'out' ? 'rgba(236, 72, 153, 0.1)' : 'rgba(59, 130, 246, 0.1)',
                            color: entry.stock_direction === 'out' ? enconPink : '#3B82F6'
                          }}
                        >
                          {entry.stock_direction === 'out' ? 'OUT' : 'IN'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            
            {selectedDayEntries.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No entries for this day
              </div>
            )}
          </div>
          
          <div className="px-6 pb-6 pt-2 border-t">
            <Button
              variant="outline"
              onClick={() => setShowDayModal(false)}
              className="w-full"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Past Week's Transfers Modal */}
      <Dialog open={showPastTransfersModal} onOpenChange={setShowPastTransfersModal}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold flex items-center gap-2" style={{ color: enconOrange }}>
              <Clock className="w-5 h-5" />
              Past Week's Transfers ({pastTransfers.length})
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {(() => {
              // Group past transfers by date
              const byDate = {};
              pastTransfers.forEach(t => {
                const d = t.status === 'complete' 
                  ? format(new Date(t.status_updated_at || t.date), 'yyyy-MM-dd')
                  : format(new Date(t.date), 'yyyy-MM-dd');
                if (!byDate[d]) byDate[d] = [];
                byDate[d].push(t);
              });
              // Sort dates newest first
              const sortedDates = Object.keys(byDate).sort((a, b) => b.localeCompare(a));
              
              return sortedDates.map(dateKey => {
                const dateTransfers = byDate[dateKey];
                const dateObj = new Date(dateKey);
                return (
                  <div key={dateKey}>
                    <h3 className="text-sm font-bold text-gray-600 mb-2 border-b pb-1">
                      {format(dateObj, 'EEEE do MMMM')}
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {dateTransfers.map(t => {
                        const direction = t.stock_direction || 'in';
                        const cardColor = t.status === 'complete' ? '#22c55e' : 
                                         t.status === 'failed' ? '#ef4444' : 
                                         direction === 'out' ? enconPink : '#3b82f6';
                        const bgColor = t.status === 'complete' ? 'rgba(34, 197, 94, 0.1)' :
                                       t.status === 'failed' ? 'rgba(239, 68, 68, 0.1)' :
                                       direction === 'out' ? 'rgba(236, 72, 153, 0.1)' : 'rgba(59, 130, 246, 0.1)';
                        return (
                          <div
                            key={t.id || t._id}
                            onClick={() => { setShowPastTransfersModal(false); openEditModal(t); }}
                            className="p-3 rounded-xl border-2 cursor-pointer hover:shadow-md transition-all"
                            style={{ borderColor: cardColor, backgroundColor: bgColor }}
                            data-testid={`past-transfer-${t.id || t._id}`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              {t.status === 'complete' && <Check className="w-4 h-4 text-green-500" />}
                              {t.status === 'failed' && <XCircle className="w-4 h-4 text-red-500" />}
                              {t.status !== 'complete' && t.status !== 'failed' && (
                                <ArrowRightLeft className="w-4 h-4" style={{ color: cardColor }} />
                              )}
                              <span className="font-bold text-sm truncate">{t.supplier_name || 'Unknown'}</span>
                            </div>
                            {t.order_number && (
                              <div className="text-xs text-gray-500">Expense: {t.order_number}</div>
                            )}
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs font-semibold capitalize" style={{ color: cardColor }}>{t.status || 'pending'}</span>
                              <span 
                                className="text-[10px] font-bold px-1.5 py-0.5 rounded text-white"
                                style={{ backgroundColor: direction === 'out' ? enconPink : '#3b82f6' }}
                              >
                                STOCK {direction === 'out' ? 'OUT' : 'IN'}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              });
            })()}
            {pastTransfers.length === 0 && (
              <p className="text-center text-gray-400 py-6">No transfers from the past 5 working days</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GoodsIn;
